
import { Card } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { Building2, Phone, Mail, Globe, CalendarClock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export interface ClientProspect {
  id: string;
  type: 'prospect' | 'client';
  name: string;
  industry: string;
  phone: string;
  email: string;
  country: string;
  logo: string;
  status: string;
  subStatus?: string;
  lastCommunicationDate?: string;
  createdAt?: string;
}

interface ClientProspectCardProps {
  data: ClientProspect;
}

export function ClientProspectCard({ data }: ClientProspectCardProps) {
  const navigate = useNavigate();

  const getAvatarColor = (name: string) => {
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500', 'bg-teal-500'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const getStatusBadge = (status: string, type: string) => {
    if (type === 'client') {
      if (status === 'active') return <Badge variant="success">Active</Badge>;
      if (status === 'archived') return <Badge variant="secondary">Archived</Badge>;
    }
    
    switch (status) {
      case 'new': return <Badge variant="info">New</Badge>;
      case 'contacted': return <Badge variant="warning">Contacted</Badge>;
      case 'interested': return <Badge variant="primary">Interested</Badge>;
      case 'converted': return <Badge variant="success">Converted</Badge>;
      case 'rejected': return <Badge variant="danger">Rejected</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const initials = data.name ? data.name.substring(0, 2).toUpperCase() : 'NA';

  const handleClick = () => {
    navigate(`/sales/clients-prospects/${data.id}`);
  };

  return (
    <Card 
      className="p-5 cursor-pointer transition-all hover:shadow-md hover:border-blue-300 relative overflow-hidden group"
      onClick={handleClick}
    >
      <div className="absolute inset-0 bg-blue-50 opacity-0 group-hover:opacity-10 transition-opacity pointer-events-none" />
      
      <div className="flex items-start gap-4">
        {data.logo ? (
          <img src={data.logo} alt={data.name} className="w-12 h-12 rounded-lg object-cover border border-gray-200" />
        ) : (
          <div className={`w-12 h-12 rounded-lg text-white flex items-center justify-center font-bold shadow-sm ${getAvatarColor(data.name)}`}>
            {initials}
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold text-gray-900 truncate" title={data.name}>
              {data.name}
            </h3>
            <Badge variant={data.type === 'client' ? 'success' : 'primary'} className="shrink-0 text-xs py-0 h-5">
              {data.type === 'client' ? 'Client' : 'Prospect'}
            </Badge>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-1 truncate">
            <Building2 className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{data.industry || 'No Industry'}</span>
          </div>
        </div>
      </div>

      <div className="mt-4 space-y-2.5">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Phone className="w-4 h-4 text-gray-400 shrink-0" />
          <span className="truncate">{data.phone || 'No Phone'}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Mail className="w-4 h-4 text-gray-400 shrink-0" />
          <span className="truncate">{data.email || 'No Email'}</span>
        </div>
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center gap-2 truncate">
            <Globe className="w-4 h-4 text-gray-400 shrink-0" />
            <span className="truncate">{data.country || 'No Country'}</span>
          </div>
          <div>
            {getStatusBadge(data.status, data.type)}
          </div>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-2 text-xs text-gray-500">
        <CalendarClock className="w-3.5 h-3.5" />
        <span>
          Last Contact: {data.lastCommunicationDate ? new Date(data.lastCommunicationDate).toLocaleDateString('en-IN') : 'Never'}
        </span>
      </div>
    </Card>
  );
}
